import { OUSTER_STUDIO_DOWNLOAD_URL } from '../../utils/constants';
import { fetchWithAuthV2 } from '../../utils/requests';
import DOMPurify from 'dompurify';

export type OsdCompatibilityPolicy = {
  min_supported_version: string;
  force_update: boolean;
  message: string;
  download_url: string;
  updated_at: string | null;
  updated_by: string | null;
};

export const OSD_COMPATIBILITY_POLICY_QUERY_KEY = [
  'osd-compatibility-policy',
] as const;

/** Admin singleton under org-1 dashboard (backend slice 2). */
export const OSD_COMPATIBILITY_POLICY_ADMIN_PATH =
  '/api/v1/organizations/1/dashboard/osd_compatibility_policy';

/**
 * Default Policy Message templates for OSD Mgmt.
 * Placeholders stay literal in the stored HTML; desktop substitutes at render:
 * - {current_version}: running app version
 * - {latest_version}: newest build from the auto-update channel (or min when unknown)
 */
export const DEFAULT_HARD_BLOCK_POLICY_MESSAGE =
  '<p>Your version of Ouster Studio ({current_version}) is deprecated and no longer supported. Update to latest version ({latest_version}) to continue using the application.</p>';

export const DEFAULT_SOFT_WARNING_POLICY_MESSAGE =
  '<p>A software update for Ouster Studio is available ({latest_version}). Support for version {current_version} will end soon. We recommend updating at your earliest convenience to access the latest features and bug fixes.</p>';

export function defaultPolicyMessageForForceUpdate(
  forceUpdate: boolean
): string {
  return forceUpdate
    ? DEFAULT_HARD_BLOCK_POLICY_MESSAGE
    : DEFAULT_SOFT_WARNING_POLICY_MESSAGE;
}

/** True when message is empty or equals either stock template (trimmed). */
export function isStockPolicyMessage(message: string): boolean {
  const normalized = message.trim();
  if (!normalized) {
    return true;
  }
  return (
    normalized === DEFAULT_HARD_BLOCK_POLICY_MESSAGE ||
    normalized === DEFAULT_SOFT_WARNING_POLICY_MESSAGE
  );
}

/** API / empty-DB shape (no stock message until admin seeds or saves). */
export const DEFAULT_OSD_COMPATIBILITY_POLICY: OsdCompatibilityPolicy = {
  min_supported_version: '0.0.0',
  force_update: false,
  message: '',
  download_url: OUSTER_STUDIO_DOWNLOAD_URL,
  updated_at: null,
  updated_by: null,
};

/**
 * Admin form baseline from a loaded policy. Empty stored message gets the
 * stock template for the current force_update mode so Save does not ship a
 * bodiless desktop modal.
 */
export function toAdminFormPolicy(
  policy: OsdCompatibilityPolicy
): OsdCompatibilityPolicy {
  const message = policy.message.trim()
    ? policy.message
    : defaultPolicyMessageForForceUpdate(policy.force_update);
  return { ...policy, message };
}

const SEMVER_PATTERN =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/;

const ALLOWED_MESSAGE_TAGS = ['a', 'p', 'br', 'strong', 'em', 'ul', 'ol', 'li'];

const HTTP_URL_PATTERN = /^https?:\/\//i;

export function isValidSemver(version: string): boolean {
  return SEMVER_PATTERN.test(version.trim());
}

export function isValidHttpUrl(value: string): boolean {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Best-effort allowlist for admin HTML preview. Server still sanitizes on save;
 * desktop sanitizes again on render.
 */
export function sanitizePolicyMessageHtml(html: string): string {
  if (!html.trim()) {
    return '';
  }

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ALLOWED_MESSAGE_TAGS,
    ALLOWED_ATTR: ['href'],
    ALLOWED_URI_REGEXP: HTTP_URL_PATTERN,
    ALLOW_ARIA_ATTR: false,
    ALLOW_DATA_ATTR: false,
  });
}

export function normalizeOsdCompatibilityPolicy(
  raw: Partial<OsdCompatibilityPolicy> | null | undefined
): OsdCompatibilityPolicy {
  return {
    min_supported_version:
      typeof raw?.min_supported_version === 'string' &&
      raw.min_supported_version.trim()
        ? raw.min_supported_version.trim()
        : DEFAULT_OSD_COMPATIBILITY_POLICY.min_supported_version,
    force_update: Boolean(raw?.force_update),
    message: typeof raw?.message === 'string' ? raw.message : '',
    download_url:
      typeof raw?.download_url === 'string' && raw.download_url.trim()
        ? raw.download_url.trim()
        : DEFAULT_OSD_COMPATIBILITY_POLICY.download_url,
    updated_at:
      typeof raw?.updated_at === 'string' || raw?.updated_at === null
        ? raw.updated_at
        : null,
    updated_by:
      typeof raw?.updated_by === 'string' || raw?.updated_by === null
        ? raw.updated_by
        : null,
  };
}

export async function fetchOsdCompatibilityPolicy(): Promise<OsdCompatibilityPolicy> {
  const response = (await fetchWithAuthV2(OSD_COMPATIBILITY_POLICY_ADMIN_PATH, {
    method: 'GET',
    fallbackErrorMessage: 'Failed to load OSD compatibility policy',
  })) as Partial<OsdCompatibilityPolicy>;

  return normalizeOsdCompatibilityPolicy(response);
}

export type SaveOsdCompatibilityPolicyInput = {
  min_supported_version: string;
  force_update: boolean;
  message: string;
  download_url: string;
};

export async function saveOsdCompatibilityPolicy(
  input: SaveOsdCompatibilityPolicyInput
): Promise<OsdCompatibilityPolicy> {
  const min_supported_version = input.min_supported_version.trim();
  const message = input.message;
  const download_url = input.download_url.trim() || OUSTER_STUDIO_DOWNLOAD_URL;

  if (!isValidSemver(min_supported_version)) {
    throw new Error('Min supported version must be a valid semver string');
  }
  if (!isValidHttpUrl(download_url)) {
    throw new Error('Download URL must be an absolute http(s) URL');
  }

  const response = (await fetchWithAuthV2(OSD_COMPATIBILITY_POLICY_ADMIN_PATH, {
    method: 'PATCH',
    fallbackErrorMessage: 'Failed to save OSD compatibility policy',
    body: JSON.stringify({
      min_supported_version,
      force_update: input.force_update,
      message,
      download_url,
    }),
  })) as Partial<OsdCompatibilityPolicy>;

  return normalizeOsdCompatibilityPolicy(response);
}

/**
 * Server stores naive UTC and serializes with datetime.isoformat() (no offset).
 * ES treats offset-less date-times as local; treat bare server stamps as UTC.
 */
export function parsePolicyTimestamp(value: string): Date {
  const trimmed = value.trim();
  if (!trimmed) {
    return new Date(Number.NaN);
  }
  // Already has Z or numeric offset (±HH:MM or ±HHMM).
  if (/[zZ]$/.test(trimmed) || /[+-]\d{2}:?\d{2}$/.test(trimmed)) {
    return new Date(trimmed);
  }
  // Date-only YYYY-MM-DD is UTC midnight per ES; leave alone.
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return new Date(trimmed);
  }
  return new Date(`${trimmed}Z`);
}

export function formatPolicyTimestamp(value: string | null): string {
  if (!value) {
    return 'Never';
  }
  const date = parsePolicyTimestamp(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}
